import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { exportToPdf } from './logic/exportPDF'
import { DroppableCell, DraggableLesson } from './logic/DragAndDrop'
import { saveTimetableToServer } from './logic/saveTimeTable'
import { useTimeSlots } from './logic/timeslotes'
import { checkScheduleConflicts, fetchRooms } from './logic/room'
import { checkTimetable } from './logic/checkTable'
import { API_TIMETABLE_URL } from '@/config/constants'
import { useAdminStore } from '@/store/adminStore'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
// Define a map to store subject colors outside the component to persist across renders
const subjectColors: Record<string, string> = {
  Mathematics: '#FF5733',
  Science: '#33FF57',
  English: '#3357FF',
  History: '#F033FF',
  Geography: '#FF33F0',
  Physics: '#33FFF0',
  Chemistry: '#F0FF33',
  Biology: '#FF3333',
  'Computer Science': '#33FF33',
  Art: '#3333FF',
  Music: '#FFAA33',
  'Physical Education': '#33FFAA',
  French: '#AA33FF',
  Spanish: '#FF33AA',
  German: '#33AAFF',
}

// Add responsive CSS styles
const responsiveStyles = `
  @media (max-width: 1024px) {
    .timetable-grid {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }
    .timetable-grid table {
      min-width: 700px;
    }
    .timetable-grid th,
    .timetable-grid td {
      min-width: 110px;
      padding: 0.5rem;
    }
  }
  @media (max-width: 768px) {
    .timetable-grid table {
      min-width: 600px;
    }
    .timetable-grid th,
    .timetable-grid td {
      min-width: 100px;
      padding: 0.4rem;
      font-size: 0.875rem;
    }
  }
  @media (max-width: 640px) {
    .timetable-grid th,
    .timetable-grid td {
      min-width: 90px;
      padding: 0.25rem;
      font-size: 0.75rem;
    }
  }
`

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  ArrowLeft,
  RefreshCw,
  Trash2,
  FileUp,
  Users,
  User,
  FileDown,
  AlertCircle,
  School,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/use-toast'
import {
  TimetableAssignment,
  TimetableData,
  UnscheduledSubject,
} from '@/types/timetable'
import { UnscheduleZone } from './UnscheduleZone'
import { DraggableUnscheduledSubject } from './DraggableUnscheduledSubject'
import {
  removeAssignmentFromTimetableData,
  addAssignmentToTimetableData,
} from '@/services/timetableService'
import { API_URL } from '@/config/constants'
import { RoomSelectionDialog } from './RoomSelectionDialog'
// import { Edit } from 'lucide-react'
import { TimetableViewTour } from '@/tour/admin/timetableViewTour'
import { TableCheckModal } from './TableCheckModal'

// Track if backend is already initialized globally
// let isBackendInitialized = false

interface TimetableDisplayProps {
  timetableData: any
  onBack?: () => void
  onRegenerate?: () => void
  onDelete?: () => void
  autoAnalyzeOnMount?: boolean
}
interface UnscheduledSubjectsMap {
  [className: string]: UnscheduledSubject[]
}
export function TimetableDisplay({
  timetableData,
  onBack,
  onRegenerate,
  onDelete,
  autoAnalyzeOnMount = false,
}: TimetableDisplayProps) {
  const { t } = useTranslation('timetable', { keyPrefix: 'timetable-folder.timetableDisplay' })
  const { 
    // startViewTour, 
    viewTour, 
    completeViewTour 
  } = useAdminStore()

  // Map French day names to English (for backend compatibility)
  const dayNameMap: Record<string, string> = {
    'Lundi': 'Monday',
    'Mardi': 'Tuesday',
    'Mercredi': 'Wednesday',
    'Jeudi': 'Thursday',
    'Vendredi': 'Friday',
    'Samedi': 'Saturday',
    'Dimanche': 'Sunday',
    'Monday': 'Monday',
    'Tuesday': 'Tuesday',
    'Wednesday': 'Wednesday',
    'Thursday': 'Thursday',
    'Friday': 'Friday',
    'Saturday': 'Saturday',
    'Sunday': 'Sunday',
  }

  // Normalize day name to English (handles both English and French from backend)
  const normalizeDay = (day: string): string => {
    return dayNameMap[day] || day
  }

  // Translate day name for display
  const translateDay = (day: string): string => {
    const normalizedDay = normalizeDay(day)
    return t(`days.${normalizedDay?.toLowerCase()}`, normalizedDay)
  }
  const { toast } = useToast()
  const [selectedClass, setSelectedClass] = useState<string>('')
  const [dataInput, setDataInput] = useState<string>('')
  const [resultInput, setResultInput] = useState<string>('')
  const [injectedData, setInjectedData] = useState<any>(null)
  const [isPdfExporting, setIsPdfExporting] = useState<boolean>(false)
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isCheckingTable, setIsCheckingTable] = useState<boolean>(false)
  const [isTableCheckModalOpen, setIsTableCheckModalOpen] = useState<boolean>(false)
  const [tableCheckResult, setTableCheckResult] = useState<{
    roomConflicts: number
    teacherDoubleBooking: number
    classDoubleBooking: number
  } | null>(null)
  const [, setRooms] = useState<
    Array<{ name: string; type: string; capacity: number }>
  >([])
  const [, setIsLoadingRooms] = useState<boolean>(false)
  const [isRoomDialogOpen, setIsRoomDialogOpen] = useState<boolean>(false)
  const [selectedLessonForRoom, setSelectedLessonForRoom] = useState<{
    lesson: any
    day: string
    time: string
  } | null>(null)
  // Replace the single unscheduled subjects state with a map organized by class
  const [unscheduledSubjectsByClass, setUnscheduledSubjectsByClass] =
    useState<UnscheduledSubjectsMap>({})
  // Keep the selected subject state, but simplify what we store
  const [selectedUnscheduledSubject, setSelectedUnscheduledSubject] =
    useState<UnscheduledSubject | null>(null)

  // Change tracking state
  const [timetableIsChanged, setTimetableIsChanged] = useState(false)
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null)
  const originalTimetableDataRef = useRef<TimetableData | null>(null)

  const timetableRef = useRef<HTMLDivElement>(null)

  // Get the data to display (either injected or from props)
  const getDisplayData = useCallback(() => {
    // Use injected data if available
    if (injectedData) {
      return injectedData
    }

    // Use timetable data from props
    if (timetableData) {
      // If timetableData has a data property with scheduleData, extract it
      if (timetableData.data && timetableData.data.scheduleData) {
        return timetableData.data
      }
      return timetableData
    }

    return null
  }, [injectedData, timetableData])

  // Wrapper for setInjectedData that tracks changes
  const setInjectedDataWithTracking = useCallback((data: any) => {
    setInjectedData(data)
    // Mark as changed when data is updated (except on initial load)
    if (originalTimetableDataRef.current) {
      setTimetableIsChanged(true)
    }
  }, [])

  // Store original data on mount or when data is first loaded
  useEffect(() => {
    const displayData = getDisplayData()
    if (displayData && !originalTimetableDataRef.current) {
      originalTimetableDataRef.current = JSON.parse(JSON.stringify(displayData)) as TimetableData
      setTimetableIsChanged(false)
    }
  }, [getDisplayData, injectedData]) // Run when data is loaded

  // Handle navigation attempt - check for unsaved changes
  const handleNavigationAttempt = useCallback((navigationCallback: () => void) => {
    if (timetableIsChanged) {
      setPendingNavigation(() => navigationCallback)
      setShowSaveDialog(true)
    } else {
      navigationCallback()
    }
  }, [timetableIsChanged])

  // Handle save and navigate
  const handleSaveAndNavigate = useCallback(async () => {
    try {
      setIsSaving(true)
      const currentData = getDisplayData()
      if (currentData) {
        await saveTimetableToServer(currentData as TimetableData, setInjectedDataWithTracking)
        // Update original data to current state after save
        originalTimetableDataRef.current = JSON.parse(JSON.stringify(currentData)) as TimetableData
        setTimetableIsChanged(false)
        toast({
          title: t('toast.timetableSaved'),
          description: t('toast.timetableSavedDescription'),
        })
      }
      setShowSaveDialog(false)
      if (pendingNavigation) {
        pendingNavigation()
        setPendingNavigation(null)
      }
    } catch (error: any) {
      // console.error('Error saving timetable:', error)
      toast({
        title: t('toast.saveFailed'),
        description: error.message || t('toast.saveFailedDescription'),
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }, [pendingNavigation, t, getDisplayData, setInjectedDataWithTracking])

  // Handle discard and navigate
  const handleDiscardAndNavigate = useCallback(() => {
    // Revert to original data
    if (originalTimetableDataRef.current) {
      setInjectedData(originalTimetableDataRef.current)
      setTimetableIsChanged(false)
    }
    setShowSaveDialog(false)
    if (pendingNavigation) {
      pendingNavigation()
      setPendingNavigation(null)
    }
  }, [pendingNavigation])

  // Wrapped navigation handlers
  const handleBack = useCallback(() => {
    if (onBack) {
      handleNavigationAttempt(() => onBack())
    }
  }, [handleNavigationAttempt, onBack])

  const handleRegenerate = useCallback(() => {
    if (onRegenerate) {
      handleNavigationAttempt(() => onRegenerate())
    }
  }, [handleNavigationAttempt, onRegenerate])

  // Add debug log for tour state
  useEffect(() => {
// console.log(`Tour active state:', viewTour.isActive)
  }, [viewTour.isActive])

  // // Handle starting the tour manually
  // const handleStartTour = useCallback(() => {
  //   console.log('Starting view tour...')
  //   completeViewTour() // Complete any existing tour
  //   setTimeout(() => {
  //     startViewTour() // Start the tour fresh
  //   }, 100)
  // }, [startViewTour, completeViewTour])

  // Tours are now managed by useTourManager hook
  // No need for manual tour triggering logic

  // Add effect to validate tour targets
  useEffect(() => {
    if (viewTour.isActive) {
  // console.log(`Tour is active, ensuring all targets are available')
      const targets = [
        'unscheduled-area',
        'class-selection',
        'timetable-grid',
        'analyze-button',
        'save-button',
        'export-button'
      ]
      
      const missingTargets = targets.filter(target => {
        const element = document.querySelector(`[data-tour="${target}"]`)
        if (!element) {
          console.warn(`Tour target not found: ${target}`)
          return true
        }
        return false
      })

      if (missingTargets.length > 0) {
        console.warn('Some tour targets are missing:', missingTargets)
        completeViewTour()
      }
    }
  }, [viewTour.isActive, completeViewTour])

  // Add debug effect to monitor tour state
  useEffect(() => {
    if (viewTour.isActive) {
  // console.log(`Tour is now active:', viewTour)
    }
  }, [viewTour])

  // Add effect to handle tour initialization
  useEffect(() => {
    if (viewTour.isActive) {
      // Ensure all tour targets are visible
      const targets = [
        'unscheduled-area',
        'class-selection',
        'timetable-grid',
        'analyze-button',
        'save-button',
        'export-button'
      ]
      
      targets.forEach(target => {
        const element = document.querySelector(`[data-tour="${target}"]`)
        if (!element) {
          console.warn(`Tour target not found: ${target}`)
        }
      })
    }
  }, [viewTour.isActive])

  // Use the useTimeSlots hook to fetch and manage time slots
  const {
    timeSlotData,
    isLoading: isLoadingTimeSlots,
    error: timeSlotsError,
  } = useTimeSlots()

  // Fetch rooms when component mounts
  const fetchAvailableRooms = useCallback(async () => {
    setIsLoadingRooms(true)
    try {
      const roomsData = await fetchRooms()
      setRooms(roomsData)
  // console.log(`Rooms loaded successfully:', roomsData)
    } catch (error) {
      // console.error('Error loading rooms:', error)
      toast({
        title: t('toast.errorLoadingRooms'),
        description: t('toast.errorLoadingRoomsDescription'),
        variant: 'destructive',
      })
    } finally {
      setIsLoadingRooms(false)
    }
  }, [toast, t])

  useEffect(() => {
    fetchAvailableRooms()
  }, [fetchAvailableRooms])

  // Show loading state or error if needed
  useEffect(() => {
    if (isLoadingTimeSlots) {
  // console.log(`Loading time slots...')
    } else if (timeSlotsError) {
      // console.error('Error loading time slots:', timeSlotsError)
      toast({
        title: t('toast.errorLoadingTimeSlots'),
        description: t('toast.errorLoadingTimeSlotsDescription'),
        variant: 'destructive',
      })
    } else {
  // console.log(`Time slots loaded successfully')
    }
  }, [isLoadingTimeSlots, timeSlotsError, toast, t])

  // Get all unique time slots across all days
  const getTimeSlots = () => {
    if (timeSlotData.length === 0) {
      console.warn('Time slots not yet loaded')
      return [] // or optionally return null and handle loading elsewhere
    }

    // Sort and format from backend
    const sortedSlots = [...timeSlotData].sort((a, b) => a.period - b.period)
    return sortedSlots.map(
      (slot) =>
        `${formatTimeForDisplay(slot.start)} => ${formatTimeForDisplay(slot.end)}`
    )
  }
  // Get all days that should be displayed in the timetable
  const getDays = () => {
    const schedule = getSelectedClassSchedule()

    // Define the standard week days that should always be shown
    const standardDays = [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday',
    ]

    if (!schedule) return standardDays

// console.log(`Schedule for selected class:', schedule)

    // Get days from the schedule
    const scheduleDays = Object.keys(schedule).filter((key) => {
      // Skip the 'class' property
      if (key === 'class') return false

      // Include all day properties, even if they're empty arrays
      return true
    })

    // Combine standard days with any additional days from the schedule
    const allDays = new Set([...standardDays, ...scheduleDays])

    // Define the correct day order - standard week order
    const dayOrder = [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday',
    ]

    // Sort the days according to the defined order
    const sortedDays = Array.from(allDays).sort((a, b) => {
      const indexA = dayOrder.indexOf(a)
      const indexB = dayOrder.indexOf(b)

      // If both days are in the order array, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB
      }

      // If only one day is in the order array, prioritize it
      if (indexA !== -1) return -1
      if (indexB !== -1) return 1

      // If neither day is in the order array, maintain alphabetical order
      return a.localeCompare(b)
    })

// console.log(`Days for timetable (sorted):', sortedDays)
    return sortedDays
  }

  const formatTimeForDisplay = (timeStr: string): string => {
    if (timeStr.includes(':')) {
      const [hours, minutes] = timeStr.split(':').map(Number)

      // If minutes are 00, just show the hour
      if (minutes === 0) {
        return hours.toString()
      } else {
        // Keep the original format for times with minutes
        return timeStr
      }
    } else {
      // Already just the hour
      return timeStr
    }
  }

  // Function to analyze the schedule and find unscheduled lessons
  const analyzeCurrentSchedule = async () => {
    try {
      setIsAnalyzing(true)
      const data = getDisplayData()

      if (!data) {
        toast({
          title: t('toast.noData'),
          description: t('toast.noDataDescription'),
          variant: 'destructive',
        })
        return
      }

  // console.log(`Analyzing schedule with data:', data)

      // Prepare the analyze-schedule request body
      // Try to get the inputData from the server if available
      let analyzeBody

      try {
        // First check if we have a timetable ID
        const timetableResponse = await fetch(
          `${API_URL}/timetable/etablissement`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          }
        )

        if (timetableResponse.ok) {
          const timetableData = await timetableResponse.json()
          if (timetableData.inputData) {
        // console.log(`Using inputData from server for analysis')
            analyzeBody = {
              data: timetableData.inputData,
              result: timetableData.data,
            }
          }
        }
      } catch (error) {
        // console.error('Error fetching timetable for inputData:', error)
      }
      // console.log(
      //   '===================schudel send data ======================= \n ' +
      //     analyzeBody
      // )

      // Fallback to using the current data if we couldn't get inputData
      if (!analyzeBody) {
    // console.log(`Using current data for analysis (no inputData available)')
        analyzeBody = {
          data: data,
          result: data,
        }
        // console.log(
        //   '===================schudel send data in if ======================= \n ' +
        //     analyzeBody
        // )
      }

  // console.log(`Sending analyze request with body:', analyzeBody)

      // Try to call the real API first
      try {
        const response = await fetch(
          `${API_TIMETABLE_URL}/analyze-schedule`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer 1d03360f4bcd533a1481905ed6bb9ae77bd650b901ee9c28bcf4dbffcc568575`,
            },
            body: JSON.stringify(analyzeBody),
          }
        )

        // Log the raw response for debugging
        const responseText = await response.text()
    // console.log(`Raw API response:', responseText)

        // Parse the response text
        let analysisResult
        try {
          analysisResult = JSON.parse(responseText)
      // console.log(`Parsed analysis result:', analysisResult)

          // Check if we got a valid array response
          if (Array.isArray(analysisResult)) {
            // Convert the API response to our UnscheduledSubject format
            const apiUnscheduledSubjects: UnscheduledSubject[] =
              analysisResult.map((item: any) => ({
                class: item.class || selectedClass,
                subject: item.subject,
                required_hours: item.required_hours || 1,
                scheduled_hours: item.scheduled_hours || 0,
                uncompleted_hours: item.uncompleted_hours || 1,
                teacherID: item.teacherID,
                teacherName: item.teacherName || item.teacher || 'Unknown',
                available_slots: item.available_slots || [],
              }))

            // console.log(
            //   'Converted unscheduled subjects:',
            //   apiUnscheduledSubjects
            // )
            // Group unscheduled subjects by class and update state
            const unscheduledByClass: UnscheduledSubjectsMap = {}
            apiUnscheduledSubjects.forEach((subject) => {
              if (!unscheduledByClass[subject.class]) {
                unscheduledByClass[subject.class] = []
              }
              unscheduledByClass[subject.class].push(subject)
            })
            setUnscheduledSubjectsByClass(unscheduledByClass)

            // If we have unscheduled subjects, select the first one to highlight available slots
            if (apiUnscheduledSubjects.length > 0) {
              setSelectedUnscheduledSubject(apiUnscheduledSubjects[0])
            } else {
              setSelectedUnscheduledSubject(null)
            }

            toast({
              title: t('toast.analysisComplete'),
              description: t('toast.analysisCompleteDescription', { count: apiUnscheduledSubjects.length }),
            })
            return
          } else {
            console.warn('API did not return an array:', analysisResult)
            // Continue to fallback
          }
        } catch (parseError) {
          // console.error('Error parsing API response:', parseError)
          // Continue to fallback
        }
      } catch (apiError) {
        // console.error('Error calling analyze-schedule API:', apiError)
        // Continue to fallback
      }

      // Fallback: Try to extract unscheduled subjects from the input data
      const inputData = data.inputData || analyzeBody?.data
      const unscheduledSubjects: UnscheduledSubject[] = []

      if (inputData && inputData.classes && Array.isArray(inputData.classes)) {
        // Extract classes and their subjects from input data
        inputData.classes.forEach((classData: any) => {
          if (classData.subjects && Array.isArray(classData.subjects)) {
            classData.subjects.forEach((subject: any) => {
              // Track how many hours of this subject are already scheduled
              let scheduledHours = 0

              // Look through the schedule data to see if this subject is scheduled
              if (data.scheduleData && Array.isArray(data.scheduleData)) {
                const classSchedule = data.scheduleData.find(
                  (c: any) => c.class === classData.className
                )

                if (classSchedule) {
                  // Check each day for this subject
                  Object.keys(classSchedule).forEach((day) => {
                    if (day === 'class') return

                    if (Array.isArray(classSchedule[day])) {
                      classSchedule[day].forEach((lesson: any) => {
                        if (
                          lesson.subject === subject.subject &&
                          (lesson.teacherID === subject.teacherID ||
                            lesson.teacherId === subject.teacherID)
                        ) {
                          scheduledHours++
                        }
                      })
                    }
                  })
                }
              }

              // If the subject is not fully scheduled, add it to unscheduled subjects
              const requiredHours = subject.hours || 1
              if (scheduledHours < requiredHours) {
                // Find the teacher name
                let teacherName = 'Unknown Teacher'
                if (inputData.teachers && Array.isArray(inputData.teachers)) {
                  const teacher = inputData.teachers.find(
                    (t: any) => t.teacherId === subject.teacherID
                  )
                  if (teacher) {
                    teacherName = teacher.teacherName
                  }
                }

                // Create available slots based on the time slots in the input data
                const availableSlots: any[] = []
                if (inputData.timeSlots) {
                  Object.keys(inputData.timeSlots).forEach((day) => {
                    if (inputData.timeSlots[day].studyTimes) {
                      inputData.timeSlots[day].studyTimes.forEach(
                        (time: string) => {
                          // Add available rooms
                          const availableRooms: string[] = []
                          if (
                            inputData.salles &&
                            Array.isArray(inputData.salles)
                          ) {
                            inputData.salles.forEach((room: any) => {
                              availableRooms.push(room.name)
                            })
                          }

                          availableSlots.push({
                            day,
                            time,
                            available_rooms: availableRooms,
                          })
                        }
                      )
                    }
                  })
                }

                unscheduledSubjects.push({
                  class: classData.className,
                  subject: subject.subject,
                  required_hours: requiredHours,
                  scheduled_hours: scheduledHours,
                  uncompleted_hours: requiredHours - scheduledHours,
                  teacherID: subject.teacherID,
                  teacherName,
                  available_slots: availableSlots,
                })
              }
            })
          }
        })
      }

      // If we found unscheduled subjects, use them
      if (unscheduledSubjects.length > 0) {
        // Group unscheduled subjects by class and update state
        const unscheduledByClass: UnscheduledSubjectsMap = {}
        unscheduledSubjects.forEach((subject) => {
          if (!unscheduledByClass[subject.class]) {
            unscheduledByClass[subject.class] = []
          }
          unscheduledByClass[subject.class].push(subject)
        })
        setUnscheduledSubjectsByClass(unscheduledByClass)

        // Select the first unscheduled subject to highlight available slots
        setSelectedUnscheduledSubject(unscheduledSubjects[0])

        toast({
          title: t('toast.analysisComplete'),
          description: t('toast.analysisCompleteDescription', { count: unscheduledSubjects.length }),
        })
      } else {
        // If no unscheduled subjects were found, show a message
        toast({
          title: t('toast.analysisComplete'),
          description: t('toast.noUnscheduledSubjects'),
        })
      }
    } catch (error) {
      // console.error('Error analyzing schedule:', error)
      toast({
        title: t('toast.analysisFailed'),
        description: t('toast.analysisFailedDescription'),
        variant: 'destructive',
      })
    } finally {
      setIsAnalyzing(false)
    }
  }

  // Function to check timetable for errors
  const handleCheckTable = async () => {
    try {
      setIsCheckingTable(true)
      setTableCheckResult(null)
      setIsTableCheckModalOpen(true)
      
      const data = getDisplayData()

      if (!data) {
        toast({
          title: t('toast.noData'),
          description: t('toast.noDataDescription'),
          variant: 'destructive',
        })
        setIsTableCheckModalOpen(false)
        return
      }

  // console.log(`Checking timetable with data:', data)
  // console.log(`Data structure:', {
      //   hasScheduleData: !!data.scheduleData,
      //   hasOriginalInputData: !!data.originalInputData,
      //   hasInputData: !!data.inputData,
      //   scheduleDataLength: data.scheduleData?.length || 0,
      //   keys: Object.keys(data)
      // })

      const result = await checkTimetable(data)
      setTableCheckResult(result)

    } catch (error) {
      // console.error('Error checking timetable:', error)
      toast({
        title: t('toast.checkFailed'),
        description: error instanceof Error ? error.message : t('toast.checkFailedDescription'),
        variant: 'destructive',
      })
      setIsTableCheckModalOpen(false)
    } finally {
      setIsCheckingTable(false)
    }
  }

  const handleUnscheduleLesson = (assignment: TimetableAssignment) => {
// console.log(`Unscheduling lesson:', assignment)

    const currentData = getDisplayData() as TimetableData
    const unscheduledId = `unscheduled-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

    // Remove the assignment from timetable data
    const updatedData = removeAssignmentFromTimetableData(
      currentData,
      assignment
    )

    // Initialize unscheduledAssignments array if it doesn't exist
    if (!updatedData.unscheduledAssignments) {
      updatedData.unscheduledAssignments = []
    }

    // Create the unscheduled assignment with all necessary details
    const unscheduledAssignment = {
      id: unscheduledId,
      subject: assignment.subject,
      teacherID: assignment.teacherID,
      teacher: assignment.teacher,
      class: assignment.class,
      day: assignment.day,
      time: assignment.time,
      salle: assignment.salle,
      originalDay: assignment.day,
      originalTime: assignment.time,
    }
    // Add the new unscheduled assignment with required group1 and group2 properties
    updatedData.unscheduledAssignments.push({
      ...unscheduledAssignment,
      group1: assignment.group1,
      group2: assignment.group2
    })

    // Update the timetable data in state
    setInjectedDataWithTracking(updatedData)

    // Create a simplified unscheduled subject with only essential information
    const newUnscheduledSubject: UnscheduledSubject = {
      id: unscheduledId,
      class: assignment.class,
      subject: assignment.subject,
      teacherID: assignment.teacherID,
      teacherName: assignment.teacher || 'Unknown',
      // Keep these for compatibility
      required_hours: 1,
      scheduled_hours: 0,
      uncompleted_hours: 1,
      originalDay: assignment.day,
      originalTime: assignment.time,
      available_slots: [
        {
          day: assignment.day,
          time: assignment.time,
          available_rooms: [assignment.salle || 'Room 102'],
        },
      ],
    }

    // Add to the class-specific unscheduled subjects map
    setUnscheduledSubjectsByClass((prev) => ({
      ...prev,
      [assignment.class]: [
        ...(prev[assignment.class] || []),
        newUnscheduledSubject,
      ],
    }))

    // Show notification
    toast({
      title: t('toast.lessonUnscheduled'),
      description: t('toast.lessonUnscheduledDescription', { subject: assignment.subject, class: assignment.class }),
    })
  }

  // Function to save timetable data to the server

  // Initialize selected class when timetable data is loaded
  // Use a ref to track if we've already set a class
  const hasSetInitialClassRef = useRef(false)

  useEffect(() => {
    const data = getDisplayData ? getDisplayData() : timetableData
// console.log(`Auto-selecting class from data:', data)

    // Only auto-select if no class is currently selected or we haven't set one yet
    if (data && (!selectedClass || !hasSetInitialClassRef.current)) {
      // Handle scheduleData format
      if (data.scheduleData && data.scheduleData.length > 0) {
        // console.log(
        //   'Auto-selecting first class from scheduleData:',
        //   data.scheduleData[0].class
        // )
        setSelectedClass(data.scheduleData[0].class)
        hasSetInitialClassRef.current = true
      }
      // Handle schedule format
      else if (data.schedule && Object.keys(data.schedule).length > 0) {
        const firstClass = Object.keys(data.schedule)[0]
    // console.log(`Auto-selecting first class from schedule:', firstClass)
        setSelectedClass(firstClass)
        hasSetInitialClassRef.current = true
      }
    }

    // Force select the first class if none is selected after a short delay
    const timer = setTimeout(() => {
      if (
        !selectedClass &&
        data &&
        data.scheduleData &&
        data.scheduleData.length > 0
      ) {
        // console.log(
        //   'Forcing selection of first class:',
        //   data.scheduleData[0].class
        // )
        setSelectedClass(data.scheduleData[0].class)
      }
    }, 500)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timetableData, injectedData])

  // Load timetable data from backend on component mount
  useEffect(() => {
    const loadTimetableFromBackend = async () => {
      try {
        // Always try to load data from backend if we don't have valid data
        const hasValidTimetableData =
          timetableData &&
          ((timetableData.scheduleData &&
            timetableData.scheduleData.length > 0) ||
            (timetableData.schedule &&
              Object.keys(timetableData.schedule).length > 0))

        const hasValidInjectedData =
          injectedData &&
          ((injectedData.scheduleData &&
            injectedData.scheduleData.length > 0) ||
            (injectedData.schedule &&
              Object.keys(injectedData.schedule).length > 0))

        // If we don't have valid data, load from backend
        if (!hasValidTimetableData && !hasValidInjectedData) {
      // console.log(`Loading timetable data from backend...')

          // Fetch the timetable data from the backend
          const response = await fetch(`${API_URL}/timetable/etablissement`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          })

      // console.log(`API Response status:', response.status)
      // console.log(`API Response headers:', Object.fromEntries(response.headers.entries()))

          if (response.ok) {
            const data = await response.json()
        // console.log(`Full API response:', data)
        // console.log(`Response data structure:', {
        //       hasData: !!data.data,
        //       dataType: typeof data.data,
        //       dataKeys: data.data ? Object.keys(data.data) : 'no data',
        //       hasScheduleData: data.data?.scheduleData ? 'yes' : 'no',
        //       scheduleDataLength: data.data?.scheduleData?.length || 0,
        //       hasEntries: data.data?.entries ? 'yes' : 'no',
        //       entriesLength: data.data?.entries?.length || 0
        //     })

            // Check if we have valid data
            if (data && data.data) {
          // console.log(`Raw data from backend:', data.data)

              // Check if the data has scheduleData array (new format)
              if (
                data.data.scheduleData &&
                Array.isArray(data.data.scheduleData) &&
                data.data.scheduleData.length > 0
              ) {
            // console.log(`Using scheduleData format from API')
                
                // Create the properly formatted data
                const formattedData = {
                  scheduleData: data.data.scheduleData,
                  name: data.description || 'Timetable',
                  academicYear: data.academicYear,
                  isActive: data.isActive,
                  id: data.id,
                }

                // Set the injected data with the converted data
                setInjectedData(formattedData)

                // Set the selected class to the first class
                if (data.data.scheduleData.length > 0) {
                  const firstClass = data.data.scheduleData[0].class
              // console.log(`Setting selected class to:', firstClass)
                  setSelectedClass(firstClass)
                }

                // Show success toast
                toast({
                  title: t('toast.timetableLoaded'),
                  description: t('toast.timetableLoadedDescription', { count: data.data.scheduleData.length }),
                })
              }
              // Check if the data has entries array (old format from port 3000)
              else if (
                data.data.entries &&
                Array.isArray(data.data.entries) &&
                data.data.entries.length > 0
              ) {
            // console.log(`Converting entries format to scheduleData format')

                // Group entries by className
                const entriesByClass = data.data.entries.reduce(
                  (acc: any, entry: any) => {
                    if (!acc[entry.className]) {
                      acc[entry.className] = {
                        class: entry.className,
                        // Initialize empty arrays for each day
                        Monday: [],
                        Tuesday: [],
                        Wednesday: [],
                        Thursday: [],
                        Friday: [],
                        Saturday: [],
                        Sunday: [],
                      }
                    }

                    // Add the entry to the appropriate day
                    if (entry.day && acc[entry.className][entry.day]) {
                      // Create a complete entry with all required fields
                      const completeEntry = {
                        id: entry.id,
                        time: entry.timeSlot || entry.time,
                        timeSlot: entry.timeSlot || entry.time,
                        subject: entry.subject,
                        teacher: entry.teacher || `Teacher ${entry.teacherId}`,
                        teacherID: entry.teacherId || entry.teacherID,
                        salle: entry.classroomId || entry.salle,
                        classroomId: entry.classroomId || entry.salle,
                        group1: entry.group1,
                        group2: entry.group2,
                        day: entry.day,
                        className: entry.className,
                      }
                      acc[entry.className][entry.day].push(completeEntry)
                    }

                    return acc
                  },
                  {}
                )

                // Convert to array format for scheduleData
                const scheduleData = Object.values(entriesByClass)
            // console.log(`Converted scheduleData:', scheduleData)

                // Create the properly formatted data
                const formattedData = {
                  scheduleData: scheduleData,
                  name: data.description || 'Timetable',
                  // Keep the original entries for reference
                  originalEntries: data.data.entries,
                }

                // Set the injected data with the converted data
                setInjectedData(formattedData)

                // Set the selected class to the first class
                if (scheduleData.length > 0) {
                  const firstClass = (scheduleData[0] as any).class
              // console.log(`Setting selected class to:', firstClass)
                  setSelectedClass(firstClass)
                }

                // Show success toast
                toast({
                  title: t('toast.timetableLoaded'),
                  description: t('toast.timetableLoadedDescription', { count: scheduleData.length }),
                })
              } else {
                // If no scheduleData or entries array, just use the data as is
            // console.log(`Using data as is, no scheduleData or entries array found')
                setInjectedData(data.data)

                // Show success toast
                toast({
                  title: t('toast.timetableLoaded'),
                  description: t('toast.timetableLoadedDescription', { count: 0 }),
                })
              }
            } else {
              console.warn('Timetable data from backend is empty or invalid')
          // console.log(`Empty data structure:', {
              //   hasData: !!data,
              //   dataKeys: data ? Object.keys(data) : 'no data',
              //   dataValue: data
              // })
              toast({
                title: t('toast.noTimetableFound'),
                description: t('toast.noTimetableFoundDescription'),
                variant: 'destructive',
              })
            }
          } else {
            // const errorText = await response.text()
            // console.error(t('errors.failedToLoadFromBackend'), response.status, response.statusText, 'Error body:', errorText)
            toast({
              title: t('toast.failedToLoadTimetable'),
              description: t('toast.failedToLoadTimetableDescription', { status: response.status, statusText: response.statusText }),
              variant: 'destructive',
            })
          }
        } else {
      // console.log(`Using existing timetable data, not loading from backend')
        }
      } catch (error) {
        // console.error('Error loading timetable data from backend:', error)
        toast({
          title: t('toast.errorLoadingTimetable'),
          description: t('toast.errorLoadingTimetableDescription'),
          variant: 'destructive',
        })
      }
    }

    // Load timetable data from backend
    loadTimetableFromBackend()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Reset selected unscheduled subject when class changes
    setSelectedUnscheduledSubject(null)
  }, [selectedClass])

  // Auto-analyze on mount if requested (e.g., after first generation)
  // Use a ref to ensure we only run this once
  const hasAnalyzedRef = useRef(false)
  useEffect(() => {
    if (autoAnalyzeOnMount && !hasAnalyzedRef.current) {
      // Only analyze once on initial mount
      analyzeCurrentSchedule()
      hasAnalyzedRef.current = true
    }
    // Only run on mount or when autoAnalyzeOnMount changes to true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAnalyzeOnMount])

  // Load unscheduled assignments when timetable data changes
  useEffect(() => {
    const data = getDisplayData()

    if (
      data &&
      data.unscheduledAssignments &&
      data.unscheduledAssignments.length > 0
    ) {
      // console.log(
      //   'Loading unscheduled assignments from data:',
      //   data.unscheduledAssignments
      // )

      // Group unscheduled assignments by class
      const unscheduledByClass: UnscheduledSubjectsMap = {}

      data.unscheduledAssignments.forEach((assignment: TimetableAssignment) => {
        const className = assignment.class

        if (!unscheduledByClass[className]) {
          unscheduledByClass[className] = []
        }

        unscheduledByClass[className].push({
          id:
            assignment.id ||
            `unscheduled-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          class: assignment.class,
          subject: assignment.subject,
          teacherID: assignment.teacherID,
          teacherName: assignment.teacher || 'Unknown',
          required_hours: 1,
          scheduled_hours: 0,
          uncompleted_hours: 1,
          originalDay: assignment.day,
          originalTime: assignment.time,
          available_slots: [], // We'll need to analyze to get available slots
        })
      })

      // Set the unscheduled subjects by class
      setUnscheduledSubjectsByClass(unscheduledByClass)
    }
  }, [timetableData, injectedData])

  // Remove the automatic analysis on timetable data changes to prevent recursion
  // We'll only analyze when the user explicitly clicks the Analyze button

  // Handle class selection
  const handleClassChange = (className: string) => {
    setSelectedClass(className)
  }

  // State for data injection dialog is defined above

  // Handle raw data injection
  const handleInjectData = () => {
    try {
      // Try to parse both data and result if provided
      let parsedData
      let parsedResult

      if (dataInput.trim()) {
        parsedData = JSON.parse(dataInput)
      }

      if (resultInput.trim()) {
        parsedResult = JSON.parse(resultInput)
        // Use result as the primary data if it has scheduleData
        if (parsedResult && parsedResult.scheduleData) {
          setInjectedData(parsedResult)
          if (parsedResult.scheduleData.length > 0) {
            setSelectedClass(parsedResult.scheduleData[0].class)
          }
          toast({
            title: t('toast.resultDataInjectedSuccessfully'),
            description: t('toast.resultDataInjectedDescription', { count: parsedResult.scheduleData.length }),
          })
          return
        }
      }

      // Fall back to using data if result doesn't have scheduleData
      if (parsedData && parsedData.scheduleData) {
        setInjectedData(parsedData)
        if (parsedData.scheduleData.length > 0) {
          setSelectedClass(parsedData.scheduleData[0].class)
        }
        toast({
          title: t('toast.dataInjectedSuccessfully'),
          description: t('toast.dataInjectedDescription', { count: parsedData.scheduleData.length }),
        })
      } else {
        toast({
          title: t('toast.invalidDataFormat'),
          description: t('toast.invalidDataFormatDescription'),
          variant: 'destructive',
        })
      }
    } catch (error) {
      // console.error('Error injecting data:', error)
      toast({
        title: t('toast.failedToParseData'),
        description: t('toast.failedToParseDataDescription'),
        variant: 'destructive',
      })
    }

    // Don't automatically analyze after injecting data
    // This will be done manually by the user clicking the Analyze button
  }


  // Get all classes from the data
  const getClasses = () => {
    const data = getDisplayData()
    if (!data) return []

    // console.log('Getting classes from data:', data)

    // Handle different data formats
    if (data.scheduleData) {
      const classes = data.scheduleData.map((item: any) => item.class)
  // console.log(`Found classes in scheduleData:', classes)
      return classes
    } else if (data.schedule) {
      const classes = Object.keys(data.schedule)
  // console.log(`Found classes in schedule:', classes)
      return classes
    }

// console.log(`No classes found in data')
    return []
  }

  // Get the schedule for the selected class
  const getSelectedClassSchedule = () => {
    const data = getDisplayData()
    if (!data) return null

    // Handle different data formats
    if (data.scheduleData) {
      const classSchedule = data.scheduleData.find(
        (item: any) => item.class === selectedClass
      )
      if (classSchedule) {
        // Process each day to ensure time formats are consistent
        const processedSchedule = { ...classSchedule }

        // Process each day's periods
        Object.keys(processedSchedule).forEach((day) => {
          if (day !== 'class' && Array.isArray(processedSchedule[day])) {
            processedSchedule[day] = processedSchedule[day].map(
              (period: any) => {
                // Skip if already processed or not a valid period
                if (!period || typeof period !== 'object') return period

                // Create a copy of the period
                const processedPeriod = { ...period }

                // Ensure time property exists and is properly formatted
                if (!processedPeriod.time) {
                  if (processedPeriod.start && processedPeriod.end) {
                    processedPeriod.time = `${processedPeriod.start} => ${processedPeriod.end}`
                  } else if (processedPeriod.period !== undefined) {
                    processedPeriod.time = formatTimeSlot(
                      processedPeriod.period
                    )
                  }
                }

                return processedPeriod
              }
            )
          }
        })

        return processedSchedule
      }
      return null
    } else if (data.schedule && data.schedule[selectedClass]) {
      // Convert the schedule format to match the scheduleData format
      const dayMap: Record<string, any[]> = {}

      data.schedule[selectedClass].forEach((item: any) => {
        if (!dayMap[item.day]) {
          dayMap[item.day] = []
        }

        // Determine the time format
        let timeValue
        if (item.time) {
          // Use the time directly if it exists
          timeValue = item.time
        } else if (item.start && item.end) {
          // Combine start and end if they exist
          timeValue = `${item.start} => ${item.end}`
        } else if (item.period !== undefined) {
          // Use period number as a fallback
          timeValue = formatTimeSlot(item.period)
        } else {
          // Default fallback
          timeValue = 'No time specified'
        }

        dayMap[item.day].push({
          time: timeValue,
          subject: item.subject,
          teacher: item.teacher,
          salle: item.room || item.salle,
        })
      })

      return {
        class: selectedClass,
        ...dayMap,
      }
    }

    return null
  }

  // Helper function to format period numbers to time format
  const formatTimeSlot = (period: number): string => {
    // Convert period numbers to time format (e.g., 1 => "8:00 => 8:45")
    const startHour = 7 + period
    const endHour = startHour + 1
    return `${startHour}:00 => ${endHour}:00`
  }

  // Helper function to get a color for a subject
  const getSubjectColor = (
    subjectName: string | null | undefined
  ): string | null => {
    if (!subjectName) return null

    // If we already have a color for this subject, return it
    if (subjectColors[subjectName]) {
      return subjectColors[subjectName]
    }

    // Otherwise, generate a random color and store it
    const colors = [
      '#FF5733',
      '#33FF57',
      '#3357FF',
      '#F033FF',
      '#FF33F0',
      '#33FFF0',
      '#F0FF33',
      '#FF3333',
      '#33FF33',
      '#3333FF',
      '#FFAA33',
      '#33FFAA',
      '#AA33FF',
      '#FF33AA',
      '#33AAFF',
    ]
    const randomColor = colors[Math.floor(Math.random() * colors.length)]
    subjectColors[subjectName] = randomColor
    return randomColor
  }

  // Helper function to determine if text should be white or black based on background color
  const getContrastColor = (hexColor: string | null): string => {
    if (!hexColor) return '#000000' // Default to black

    // Convert hex to RGB
    const r = parseInt(hexColor.slice(1, 3), 16)
    const g = parseInt(hexColor.slice(3, 5), 16)
    const b = parseInt(hexColor.slice(5, 7), 16)

    // Calculate luminance - <url id="" type="url" status="" title="" wc="">https://www.w3.org/TR/WCAG20-TECHS/G17.html#G17-tests</url>
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

    // Return white for dark colors, black for light colors
    return luminance > 0.5 ? '#000000' : '#FFFFFF'
  }

  // Get lesson for a specific day and time
  const getLesson = (day: string, time: string) => {
    const schedule = getSelectedClassSchedule()
    if (!schedule || !schedule[day]) return null

    // Make sure schedule[day] is an array
    if (!Array.isArray(schedule[day])) {
  // console.log(`Schedule for ${day} is not an array:`, schedule[day])
      return null
    }

    // Find the lesson with the matching time (check both time and timeSlot fields)
    // Also handle missing fields by adding them if needed
    const lesson = schedule[day].find(
      (lesson: any) =>
        lesson && (lesson.time === time || lesson.timeSlot === time)
    )

    // If we found a lesson, make sure it has all the required fields
    if (lesson) {
      // Add missing fields if needed
      if (!lesson.time && lesson.timeSlot) {
        lesson.time = lesson.timeSlot
      } else if (!lesson.timeSlot && lesson.time) {
        lesson.timeSlot = lesson.time
      }

      if (!lesson.salle && lesson.classroomId) {
        lesson.salle = lesson.classroomId
      } else if (!lesson.classroomId && lesson.salle) {
        lesson.classroomId = lesson.salle
      }

      if (!lesson.teacher && lesson.teacherId) {
        lesson.teacher = `Teacher ${lesson.teacherId}`
      }

      if (!lesson.teacherID && lesson.teacherId) {
        lesson.teacherID = lesson.teacherId
      } else if (!lesson.teacherId && lesson.teacherID) {
        lesson.teacherId = lesson.teacherID
      }
    }

    // if (lesson) {
    //   console.log(`Found lesson for ${day} at ${time}:`, lesson)
    // }

    return lesson
  }

  // Export timetable as PDF
  const handleExportPdf = async () => {
    await exportToPdf(
      getClasses,
      getDisplayData,
      selectedClass,
      setSelectedClass,
      setIsPdfExporting,
      timeSlotData // Pass timeSlotData to ensure consistent time formatting
    )
  }

  // Render the timetable
  const renderTimetable = () => {
    const days = getDays()
    const timeSlots = getTimeSlots()

    // console.log(
    //   'Rendering timetable with days:',
    //   days,
    //   'and time slots:',
    //   timeSlots
    // )

    // Always show the timetable, even if there are no days or time slots
    // This will use the default time slots if none are found
    if (days.length === 0) {
  // console.log(`No days found for rendering timetable')
      return (
        <div className="text-center p-8">
          <p className="text-muted-foreground">
            {t('classSelection.noScheduleData')}
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            {t('classSelection.tryGenerating')}
          </p>
        </div>
      )
    }

    return (
      <div
        className="overflow-x-auto timetable-grid"
        ref={timetableRef}
        key={`grid-${selectedClass}`}
      >
        <table className="w-full border-collapse min-w-[700px] md:min-w-[800px]">
          <thead>
            <tr>
              <th className="border p-2 bg-muted font-medium text-xs md:text-sm">{t('time')}</th>
              {days.map((day) => (
                <th
                  key={day}
                  className="border p-2 bg-muted font-medium min-w-[100px] md:min-w-[120px] lg:min-w-[150px] text-xs md:text-sm"
                >
                  {translateDay(day)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {timeSlots.map((time) => {
              // Check if this timeslot is a break
              const isBreak = timeSlotData.some((slot) => {
                const formattedTime = `${formatTimeForDisplay(slot.start)} => ${formatTimeForDisplay(slot.end)}`
                return formattedTime === time && slot.isBreak
              })

              return (
                <tr key={time} className={isBreak ? 'bg-amber-50' : ''}>
                  <td
                    className={`border p-2 md:p-2 text-xs md:text-sm font-medium ${
                      isBreak ? 'bg-amber-100 text-amber-900' : 'bg-muted/20'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <span className="whitespace-nowrap">{time}</span>
                      {isBreak && (
                        <span className="inline-flex items-center rounded-full bg-amber-100 px-2 md:px-2.5 py-0.5 text-xs font-medium text-amber-800 flex-shrink-0">
                          {t('break')}
                        </span>
                      )}
                    </div>
                  </td>
                  {days.map((day) => {
                    const lesson = getLesson(day, time)
                    return (
                      <td
                        key={`${day}-${time}`}
                        className="border p-1 md:p-2 align-top min-w-[120px]"
                      >
                        {isBreak ? (
                          <div className="text-center text-amber-800 text-xs sm:text-sm">
                            {t('unscheduledSubjects.breakTime')}
                          </div>
                        ) : lesson && (lesson.group1 || lesson.group2) ? (
                          <div
                            className="rounded-md p-1 md:p-2"
                            style={{
                              backgroundColor: 'rgba(235, 245, 255, 0.5)',
                            }}
                          >
                            {/* Group 1 */}
                            {lesson.group1 && (
                              <div className="mb-1 md:mb-2">
                                <div className="font-semibold text-xs md:text-sm">
                                  <Badge
                                    variant="outline"
                                    className="mr-1 text-xs"
                                    style={{
                                      backgroundColor:
                                        getSubjectColor(
                                          lesson.group1.subject
                                        ) || '#e2f0ff',
                                      borderColor:
                                        getSubjectColor(
                                          lesson.group1.subject
                                        ) || '#e2f0ff',
                                      color: getContrastColor(
                                        getSubjectColor(
                                          lesson.group1.subject
                                        ) || '#e2f0ff'
                                      ),
                                    }}
                                  >
                                    {t('unscheduledSubjects.group1')}
                                  </Badge>
                                  {lesson.group1.subject}
                                </div>
                                <div className="text-xs text-gray-600 flex items-center mt-1">
                                  <User className="h-3 w-3 mr-1" />
                                  {lesson.group1.teacher}
                                </div>
                                {lesson.group1.salle && (
                                  <div className="text-xs text-gray-600">
                                    {lesson.group1.salle}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Group 2 */}
                            {lesson.group2 && (
                              <div
                                className={
                                  lesson.group1
                                    ? 'pt-1 border-t border-dashed border-gray-300'
                                    : ''
                                }
                              >
                                <div className="font-semibold text-xs md:text-sm">
                                  <Badge
                                    variant="outline"
                                    className="mr-1 text-xs"
                                    style={{
                                      backgroundColor:
                                        getSubjectColor(
                                          lesson.group2.subject
                                        ) || '#e8f5e9',
                                      borderColor:
                                        getSubjectColor(
                                          lesson.group2.subject
                                        ) || '#e8f5e9',
                                      color: getContrastColor(
                                        getSubjectColor(
                                          lesson.group2.subject
                                        ) || '#e8f5e9'
                                      ),
                                    }}
                                  >
                                    {t('unscheduledSubjects.group2')}
                                  </Badge>
                                  {lesson.group2.subject}
                                </div>
                                <div className="text-xs text-gray-600 flex items-center mt-1">
                                  <User className="h-3 w-3 mr-1" />
                                  {lesson.group2.teacher}
                                </div>
                                {lesson.group2.salle && (
                                  <div className="text-xs text-gray-600">
                                    {lesson.group2.salle}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <DroppableCell
                            day={day}
                            time={time}
                            selectedClass={selectedClass}
                            selectedUnscheduledSubject={
                              selectedUnscheduledSubject
                            }
                            handleRescheduleUnscheduledSubject={
                              handleRescheduleUnscheduledSubject
                            }
                            setSelectedUnscheduledSubject={
                              setSelectedUnscheduledSubject
                            }
                            getDisplayData={getDisplayData}
                            setInjectedData={setInjectedData}
                          >
                            <div className="h-full w-full relative">
                              <DraggableLesson
                                lesson={lesson}
                                day={day}
                                time={time}
                                selectedClass={selectedClass}
                                getSubjectColor={getSubjectColor}
                                getContrastColor={getContrastColor}
                                handleUnscheduleLesson={handleUnscheduleLesson}
                                onEditRoom={(lesson:any, day:any, time:any) => {
                                  setSelectedLessonForRoom({
                                    lesson,
                                    day,
                                    time,
                                  })
                                  setIsRoomDialogOpen(true)
                                }}
                              />
                              {lesson && (
                                <div className="absolute top-1 right-1 flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 md:h-6 md:w-6 text-xs hover:bg-blue-50 hover:text-blue-600 min-h-[32px] min-w-[32px] md:min-h-[24px] md:min-w-[24px]"
                                    onClick={() => {
                                      setSelectedLessonForRoom({
                                        lesson,
                                        day,
                                        time,
                                      })
                                      setIsRoomDialogOpen(true)
                                    }}
                                    title={t('buttons.changeRoom')}
                                  >
                                    <School className="h-4 w-4 md:h-3 md:w-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </DroppableCell>
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  // Handle room selection
  const handleRoomSelected = async (roomName: string) => {
    if (!selectedLessonForRoom?.lesson) return

    const { lesson, day, time } = selectedLessonForRoom
    const currentData = getDisplayData()

    // Check for conflicts
    const conflict = checkScheduleConflicts(
      currentData,
      day,
      time,
      lesson.teacherId,
      roomName,
      selectedClass
    )

    if (conflict) {
      toast({
        title: t('toast.schedulingConflict'),
        description: conflict.message,
        variant: 'destructive',
      })
      return
    }

    // Proceed with room change if no conflicts
    const updatedData = JSON.parse(JSON.stringify(currentData))

    const classSchedule = updatedData.scheduleData.find(
      (schedule: any) => schedule.class === selectedClass
    )

    if (classSchedule && classSchedule[day]) {
      const lessonIndex = classSchedule[day].findIndex(
        (l: any) => l.time === selectedLessonForRoom.time
      )

      if (lessonIndex !== -1) {
        classSchedule[day][lessonIndex].salle = roomName
        setInjectedDataWithTracking(updatedData)

        toast({
          title: t('toast.roomUpdated'),
          description: t('toast.roomUpdatedDescription', { roomName }),
        })
      }
    }

    setIsRoomDialogOpen(false)
  }

  // Function to handle rescheduling an unscheduled subject
  const handleRescheduleUnscheduledSubject = (
    subject: UnscheduledSubject,
    day: string,
    time: string,
    room?: string
  ) => {
    const currentData = getDisplayData()

    // Only check conflicts if we have both teacherID and room
    if (subject.teacherID && room) {
      const conflict = checkScheduleConflicts(
        currentData,
        day,
        time,
        String(subject.teacherID),
        room,
        subject.class
      )

      if (conflict) {
        toast({
          title: t('toast.schedulingConflict'),
          description: conflict.message,
          variant: 'destructive',
        })
        return
      }
    }

    // Rest of the function remains the same as in the old version
    // console.log(
    //   'Rescheduling subject:',
    //   subject,
    //   'to day:',
    //   day,
    //   'time:',
    //   time,
    //   'room:',
    //   room
    // )

    const newAssignment: TimetableAssignment = {
      subject: subject.subject,
      teacherID: subject.teacherID,
      teacher: subject.teacherName,
      class: subject.class,
      day,
      time,
      salle: room,
      group1: undefined,
      group2: undefined
    }

    const updatedData = addAssignmentToTimetableData(
      currentData,
      newAssignment,
      room
    )

    if (updatedData.unscheduledAssignments) {
      updatedData.unscheduledAssignments =
        updatedData.unscheduledAssignments.filter(
          (item) => item.id !== subject.id
        )
    }

    setInjectedDataWithTracking(updatedData)
    setUnscheduledSubjectsByClass((prev) => ({
      ...prev,
      [subject.class]: (prev[subject.class] || []).filter(
        (s) => s.id !== subject.id
      ),
    }))
    setSelectedUnscheduledSubject(null)

    toast({
      title: t('toast.subjectScheduled'),
      description: t('toast.subjectScheduledDescription', { subject: subject.subject, day, time, class: subject.class }),
    })
  }
  return (
    <>
      <style>{responsiveStyles}</style>
      <div className="flex flex-col xl:flex-row gap-4 w-full max-w-full" key={selectedClass}>
        {/* Left sidebar with unscheduled subjects */}
        <div className="w-full xl:w-64 flex-shrink-0" data-tour="unscheduled-area">
          <UnscheduleZone onUnschedule={handleUnscheduleLesson} />
          {unscheduledSubjectsByClass[selectedClass] &&
            unscheduledSubjectsByClass[selectedClass].length > 0 && (
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="text-sm">
                    {t('unscheduledSubjects.title', { className: selectedClass })}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {unscheduledSubjectsByClass[selectedClass].map(
                      (subject) => (
                        <DraggableUnscheduledSubject
                          key={subject.id}
                          subject={subject}
                          onReschedule={handleRescheduleUnscheduledSubject}
                          isSelected={selectedUnscheduledSubject?.id === subject.id}
                          onSelect={() => {
                            if (selectedUnscheduledSubject?.id === subject.id) {
                              setSelectedUnscheduledSubject(null)
                            } else {
                              setSelectedUnscheduledSubject(subject)
                            }
                          }}
                        />
                      )
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
        </div>

        {/* Main timetable area */}
        <Card className="w-full min-w-0 flex-1 overflow-hidden" key={`timetable-${selectedClass}`}>
          <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <CardTitle className="timetable-header text-lg md:text-xl">{t('headers.timetableView')}</CardTitle>
              <CardDescription className="text-sm truncate">
                {getDisplayData()?.name || t('headers.schoolTimetable')}
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
             
              {onBack && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBack}
                  className="back-to-dashboard"
                >
                  <ArrowLeft className="h-4 w-4 md:mr-1" />
                  <span className="hidden md:inline">{t('headers.back')}</span>
                </Button>
              )}
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="min-h-[44px]">
                    <FileUp className="h-4 w-4 md:mr-1" />
                    <span className="hidden md:inline">{t('headers.injectData')}</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw] md:w-full">
                  <DialogHeader>
                    <DialogTitle>{t('dialogs.injectTimetableData')}</DialogTitle>
                  </DialogHeader>
                  <div className="py-4 space-y-4">
                    <div>
                      <h3 className="text-sm font-medium mb-2">
                        {t('dialogs.dataOriginal')}
                      </h3>
                      <Textarea
                        placeholder={t('dialogs.dataPlaceholder')}
                        className="min-h-[150px] font-mono text-sm"
                        value={dataInput}
                        onChange={(e) => setDataInput(e.target.value)}
                      />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium mb-2">
                        {t('dialogs.resultModified')}
                      </h3>
                      <Textarea
                        placeholder={t('dialogs.resultPlaceholder')}
                        className="min-h-[150px] font-mono text-sm"
                        value={resultInput}
                        onChange={(e) => setResultInput(e.target.value)}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={handleInjectData}>{t('dialogs.injectData')}</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <div className="flex space-x-2">
                {onRegenerate && (
                  <Button variant="outline" size="sm" onClick={handleRegenerate} className="min-h-[44px]">
                    <RefreshCw className="md:mr-2 h-4 w-4" />
                    <span className="hidden md:inline">{t('headers.regenerate')}</span>
                  </Button>
                )}
              </div>
              {onDelete && (
                <Button variant="outline" size="sm" onClick={onDelete} className="min-h-[44px]">
                  <Trash2 className="h-4 w-4 md:mr-1" />
                  <span className="hidden md:inline">{t('headers.delete')}</span>
                </Button>
              )}
            </div>
          </CardHeader>

          <CardContent className="space-y-2">
            {/* Class selection area */}
            <div className="flex flex-col space-y-2" data-tour="class-selection">
              <div className="flex items-center">
                <Users className="h-5 w-5 mr-2 text-muted-foreground flex-shrink-0" />
                <h3 className="text-lg font-medium">{t('classSelection.title')}</h3>
              </div>
              <ScrollArea className="whitespace-nowrap pb-2">
                <div className="flex gap-2 px-1">
                  {getClasses().map((className: string) => (
                    <Button
                      key={className}
                      variant={selectedClass === className ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleClassChange(className)}
                      className="min-h-[44px] min-w-[80px] flex-shrink-0"
                    >
                      {className}
                    </Button>
                  ))}
                </div>
              </ScrollArea>
            </div>

            <Separator className="my-1" />

            {/* Timetable grid */}
            <div className="relative" data-tour="timetable-grid">
              <ScrollArea className="h-[calc(100vh-500px)] max-h-[400px] md:max-h-[500px] lg:max-h-[600px] xl:max-h-[700px]">
                {renderTimetable()}
              </ScrollArea>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col md:flex-row justify-between gap-4 mt-1">
              <div className="flex flex-wrap gap-2">
                <Button
                  data-tour="analyze-button"
                  variant="outline"
                  size="sm"
                  onClick={analyzeCurrentSchedule}
                  disabled={isAnalyzing}
                  className="min-h-[44px] flex-1 md:flex-initial"
                >
                  {isAnalyzing ? (
                    <>
                      <RefreshCw className="h-4 w-4 md:mr-1 animate-spin" />
                      <span className="hidden md:inline">{t('buttons.analyzing')}</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-4 w-4 md:mr-1" />
                      <span className="hidden md:inline">{t('buttons.analyzeSchedule')}</span>
                    </>
                  )}
                </Button>

                <Button
                  data-tour="check-table-button"
                  variant="outline"
                  size="sm"
                  onClick={handleCheckTable}
                  disabled={isCheckingTable}
                  className="min-h-[44px] flex-1 md:flex-initial"
                >
                  {isCheckingTable ? (
                    <>
                      <RefreshCw className="h-4 w-4 md:mr-1 animate-spin" />
                      <span className="hidden md:inline">{t('buttons.checking')}</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-4 w-4 md:mr-1" />
                      <span className="hidden md:inline">{t('buttons.checkTable')}</span>
                    </>
                  )}
                </Button> 
                <Button
                  data-tour="save-button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsSaving(true)
                    saveTimetableToServer(getDisplayData() as TimetableData, setInjectedData)
                      .then(() => {
                        toast({
                          title: t('toast.timetableSaved'),
                          description: t('toast.timetableSavedDescription'),
                        })
                      })
                      .catch((error) => {
                        // console.error('Error saving timetable:', error)
                        toast({
                          title: t('toast.saveFailed'),
                          description: error.message || t('toast.saveFailedDescription'),
                          variant: 'destructive',
                        })
                      })
                      .finally(() => setIsSaving(false))
                  }}
                  disabled={isSaving}
                  className="min-h-[44px] flex-1 md:flex-initial"
                >
                  {isSaving ? (
                    <>
                      <RefreshCw className="h-4 w-4 md:mr-1 animate-spin" />
                      <span className="hidden md:inline">{t('buttons.saving')}</span>
                    </>
                  ) : (
                    <>
                      <FileUp className="h-4 w-4 md:mr-1" />
                      <span className="hidden md:inline">{t('buttons.saveToServer')}</span>
                    </>
                  )}
                </Button>
              </div>

              <Button
                data-tour="export-button"
                variant="outline"
                size="sm"
                onClick={handleExportPdf}
                disabled={isPdfExporting}
                className="min-h-[44px] w-full md:w-auto"
              >
                {isPdfExporting ? (
                  <>
                    <RefreshCw className="h-4 w-4 md:mr-1 animate-spin" />
                    <span className="hidden md:inline">{t('buttons.exporting')}</span>
                  </>
                ) : (
                  <>
                    <FileDown className="h-4 w-4 md:mr-1" />
                    <span className="hidden md:inline">{t('buttons.exportAsPdf')}</span>
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        <TimetableViewTour />
        <RoomSelectionDialog
          open={isRoomDialogOpen}
          onOpenChange={setIsRoomDialogOpen}
          onSelect={handleRoomSelected}
          currentRoom={selectedLessonForRoom?.lesson?.salle || ''}
          timetableData={getDisplayData()}
          currentClass={selectedClass}
          day={selectedLessonForRoom?.day || ''}
          time={selectedLessonForRoom?.time || ''}
        />
        <TableCheckModal
          open={isTableCheckModalOpen}
          onOpenChange={setIsTableCheckModalOpen}
          result={tableCheckResult}
          isLoading={isCheckingTable}
        />

        {/* Save Confirmation Dialog */}
        <AlertDialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('dialogs.unsavedChanges') || 'Unsaved Changes'}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('dialogs.unsavedChangesDescription') || 'You have unsaved changes. Would you like to save before leaving?'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => {
                setShowSaveDialog(false)
                setPendingNavigation(null)
              }}>
                {t('buttons.cancel') || 'Cancel'}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDiscardAndNavigate}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t('buttons.leaveWithoutSaving') || 'Leave without saving'}
              </AlertDialogAction>
              <AlertDialogAction
                onClick={handleSaveAndNavigate}
                disabled={isSaving}
              >
                {isSaving ? (t('buttons.saving') || 'Saving...') : (t('buttons.save') || 'Save')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </>
  )
}
